/**
 * App-wide handling of failed requests, on the ONE React Query client every hook shares
 * (@inferrex/api-client's `queryClient`, so the api-client's own invalidations reach it).
 *
 *   - A rejected session (401) sends the browser to sign in, once, from wherever it happened.
 *   - A failed ACTION (mutation) is a toast with the server's message and a retry only when the
 *     server said a retry could work (COMPONENTS.md). A mutation that shows its own error — a form
 *     marking fields, a confirm panel — opts out with `meta: { handlesError: true }`.
 *   - A failed page LOAD is not toasted: the page shows it in place (ProblemBlock / ErrorBanner).
 */
import type { QueryClient } from '@tanstack/react-query';
import { isSessionExpiredError, notifyProblem, redirectToLogin } from '@inferrex/ui';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { handlesError?: boolean };
  }
}

export function installErrorHandling(client: QueryClient, redirect: () => boolean = redirectToLogin): void {
  client.getQueryCache().config.onError = (error) => {
    if (isSessionExpiredError(error)) redirect();
  };
  client.getMutationCache().config.onError = (error, variables, _context, mutation) => {
    if (isSessionExpiredError(error) && redirect()) return;
    if (mutation.meta?.handlesError) return;
    // Retrying re-runs the same mutation with the same variables.
    notifyProblem(error, () => { void mutation.execute(variables); });
  };
}
